import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HandHeart, GraduationCap, Users } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { useLanguage } from '../context/LanguageContext';
export function HomePage() {
  const {
    t,
    isRTL
  } = useLanguage();
  return <>
      <SEO title={t('nav.home')} description={t('home.hero.description')} data-id="element-414" />

      {/* Hero Section */}
      <div className="relative bg-gray-900 text-white overflow-hidden min-h-[70vh] md:min-h-[75vh] lg:min-h-[80vh] flex items-center" data-id="element-415">
        <div className="absolute inset-0" data-id="element-416">
          <img src="https://cdn.magicpatterns.com/uploads/rgrSJawmhW3zF59H7uaMNf/Khabar_medical_college.jpg" alt="Khyber Medical College campus" className="h-full w-full object-cover object-center" data-id="element-417" />
          <div className="absolute inset-0 bg-black/25" data-id="element-418" />
        </div>

        <div className="relative container mx-auto px-4 md:px-6 py-18 md:py-24" data-id="element-419">
          <FadeIn className="max-w-4xl mx-auto" data-id="element-420">
            {/* Glassmorphic Content Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl" data-id="element-421">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-4 md:mb-6 text-white" style={{
              textShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
            }} data-id="element-422">
                {t('home.hero.title')}
                <br data-id="element-423" />
                {t('home.hero.subtitle')}
              </h1>
              <p className="text-base md:text-lg text-white/95 leading-relaxed" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
            }} data-id="element-424">
                {t('home.hero.description')}
              </p>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Intro Section - History & Purpose */}
      <Section background="white" data-id="element-425">
        <div className="max-w-4xl mx-auto" data-id="element-426">
          <FadeIn className="text-center mb-12" data-id="element-427">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-primary-900" data-id="element-428">
              {t('home.whoWeAre.title')}
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed space-y-4" data-id="element-429">
              <p data-id="element-430">{t('home.whoWeAre.p1')}</p>
              <p data-id="element-431">{t('home.whoWeAre.p2')}</p>
            </div>
          </FadeIn>

          {/* Important Statement */}
          <FadeIn delay={0.2} data-id="element-432">
            <div className="bg-accent-50 border-l-4 border-accent-600 p-6 rounded-r-lg" data-id="element-433">
              <p className="text-gray-800 font-medium text-center" data-id="element-434">
                {t('home.whoWeAre.statement')}
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Link Blocks - Key Areas */}
      <Section background="gray" data-id="element-435">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-436">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-primary-900" data-id="element-437">
            {t('home.whatWeOffer.title')}
          </h2>
          <p className="text-gray-600" data-id="element-438">{t('home.whatWeOffer.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-439">
          <FadeIn delay={0} data-id="element-440">
            <Link to="/membership" className="group" data-id="element-441">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary-200" data-id="element-442">
                <CardHeader className="text-center pb-4" data-id="element-443">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-444">
                    <GraduationCap className="h-8 w-8" data-id="element-445" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-446">
                    {t('home.whatWeOffer.membership.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-447">
                  <CardDescription className="text-base mb-4" data-id="element-448">
                    {t('home.whatWeOffer.membership.description')}
                  </CardDescription>
                  <div className="text-primary-700 font-medium group-hover:underline inline-flex items-center" data-id="element-449">
                    {t('common.learnMore')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-450" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-451">
            <Link to="/charity" className="group" data-id="element-452">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-200" data-id="element-453">
                <CardHeader className="text-center pb-4" data-id="element-454">
                  <div className="h-16 w-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-455">
                    <HandHeart className="h-8 w-8" data-id="element-456" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-457">
                    {t('home.whatWeOffer.charity.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-458">
                  <CardDescription className="text-base mb-4" data-id="element-459">
                    {t('home.whatWeOffer.charity.description')}
                  </CardDescription>
                  <div className="text-green-700 font-medium group-hover:underline inline-flex items-center" data-id="element-460">
                    {t('common.viewProjects')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-461" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-462">
            <Link to="/events" className="group" data-id="element-463">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-accent-200" data-id="element-464">
                <CardHeader className="text-center pb-4" data-id="element-465">
                  <div className="h-16 w-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-466">
                    <Users className="h-8 w-8" data-id="element-467" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-468">
                    {t('home.whatWeOffer.social.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-469">
                  <CardDescription className="text-base mb-4" data-id="element-470">
                    {t('home.whatWeOffer.social.description')}
                  </CardDescription>
                  <div className="text-accent-700 font-medium group-hover:underline inline-flex items-center" data-id="element-471">
                    {t('common.seeEvents')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-472" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>
        </div>
      </Section>

      {/* Recent Activity Preview */}
      <Section data-id="element-473">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-474">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-primary-900" data-id="element-475">
            {t('home.recentImpact.title')}
          </h2>
          <p className="text-gray-600" data-id="element-476">{t('home.recentImpact.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-477">
          {[{
          title: t('home.recentImpact.floodRelief.title'),
          image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          desc: t('home.recentImpact.floodRelief.description')
        }, {
          title: t('home.recentImpact.eyeCamp.title'),
          image: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          desc: t('home.recentImpact.eyeCamp.description')
        }, {
          title: t('home.recentImpact.education.title'),
          image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          desc: t('home.recentImpact.education.description')
        }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-478">
              <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow" data-id="element-479">
                <div className="h-48 overflow-hidden" data-id="element-480">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-481" />
                </div>
                <CardContent className="flex-1 p-6" data-id="element-482">
                  <h3 className="text-xl font-serif font-bold mb-2 text-primary-900" data-id="element-483">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-484">{item.desc}</p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>
    </>;
}